import { useState } from "react";
import axios from "axios";
import { UserPlus, Loader2, Check, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferFriendWidget({ variant = "card", shareUrl }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    friend_name: "",
    friend_email: "",
    from_name: "",
    from_email: "",
    personal_message: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.friend_name.trim() || !form.friend_email.includes("@")) {
      setError("Please fill in your friend's name and a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/refer`, {
        ...form,
        share_url: shareUrl || window.location.origin,
      });
      setSuccess(true);
      setForm({ friend_name: "", friend_email: "", from_name: "", from_email: "", personal_message: "" });
      setTimeout(() => { setSuccess(false); setOpen(false); }, 2400);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not send referral. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (variant === "inline") {
    return (
      <button onClick={() => setOpen(true)} data-testid="btn-refer-friend-inline"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-700 hover:text-zinc-900 font-semibold">
        <UserPlus size={14} /> Refer a friend
      </button>
    );
  }

  return (
    <>
      <div className="bg-gradient-to-br from-zinc-950 to-zinc-800 rounded-3xl p-6 text-white" data-testid="refer-friend-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <UserPlus size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-base">Know someone building a product?</div>
            <p className="text-xs text-zinc-400 mt-1">Send them FastPrototype.ai. We'll email a short intro on your behalf.</p>
            <button onClick={() => setOpen(true)} data-testid="btn-open-refer"
              className="mt-3 inline-flex items-center gap-1.5 bg-white text-zinc-950 text-xs font-semibold px-4 py-2 rounded-full hover:bg-zinc-100">
              <UserPlus size={12} /> Refer a friend
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4" onClick={() => !submitting && setOpen(false)} data-testid="refer-modal">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <UserPlus size={18} className="text-zinc-700" />
                <h3 className="font-bold text-zinc-950">Refer a friend</h3>
              </div>
              <button onClick={() => setOpen(false)} disabled={submitting}
                className="p-1 hover:bg-zinc-100 rounded-lg text-zinc-500"><X size={16} /></button>
            </div>
            {success ? (
              <div className="text-center py-8" data-testid="refer-success">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Check size={22} className="text-emerald-700" />
                </div>
                <div className="font-bold text-zinc-950">Referral sent — thanks!</div>
                <p className="text-sm text-zinc-600 mt-1">We've emailed your friend with a short intro.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="refer-form">
                <div>
                  <label className="text-xs font-semibold text-zinc-600">Friend's name *</label>
                  <input required data-testid="input-friend-name" value={form.friend_name} onChange={e => setForm({ ...form, friend_name: e.target.value })}
                    className="mt-1 w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-zinc-600">Friend's email *</label>
                  <input required type="email" data-testid="input-friend-email" value={form.friend_email} onChange={e => setForm({ ...form, friend_email: e.target.value })}
                    placeholder="friend@example.com"
                    className="mt-1 w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-zinc-600">Your name</label>
                    <input data-testid="input-from-name" value={form.from_name} onChange={e => setForm({ ...form, from_name: e.target.value })}
                      className="mt-1 w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-zinc-600">Your email</label>
                    <input type="email" data-testid="input-from-email" value={form.from_email} onChange={e => setForm({ ...form, from_email: e.target.value })}
                      className="mt-1 w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-zinc-600">Short note (optional)</label>
                  <textarea rows={3} data-testid="input-personal-message" value={form.personal_message} onChange={e => setForm({ ...form, personal_message: e.target.value })}
                    placeholder="Hey — thought you'd find this useful…"
                    className="mt-1 w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 resize-none" />
                </div>
                {error && <div data-testid="refer-error" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
                <button type="submit" disabled={submitting} data-testid="btn-submit-refer"
                  className="w-full py-3 bg-zinc-950 text-white rounded-full font-semibold text-sm hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center justify-center gap-2">
                  {submitting ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : "Send referral email"}
                </button>
                <p className="text-[11px] text-zinc-500 text-center">We never add them to marketing lists — this is a one-off email.</p>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
